/**
 * Cart Summary Card - Pricing breakdown with discounts
 * Design: Clean tabular layout, mono numbers, semantic colors
 */
import { Tag, TrendingDown, DollarSign } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { formatCartPrice } from '../utils/cartFormatters';

export function CartSummaryCard({ pricing }) {
  const hasDiscount = pricing.discount_amount > 0;
  const breakdown = pricing.breakdown || {};

  return (
    <Card className="border-2" data-testid="cart-summary-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm heading-font flex items-center gap-2">
          <DollarSign className="w-4 h-4" strokeWidth={1.5} />
          Order Summary
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Subtotal */}
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">Subtotal</span>
          <span className="mono font-medium" data-testid="cart-subtotal">
            {formatCartPrice(pricing.subtotal)}
          </span>
        </div>

        {/* Discount Section */}
        {hasDiscount && (
          <>
            <Separator />
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Tag className="w-3.5 h-3.5 text-green-600" strokeWidth={1.5} />
                <span className="text-sm font-medium text-green-600">Multi-booking Discount</span>
              </div>
              
              <div className="bg-green-50 dark:bg-green-950/20 rounded-lg p-3 space-y-1.5">
                {breakdown.multi_booking_discount > 0 && (
                  <div className="flex justify-between text-xs">
                    <span className="text-muted-foreground">
                      {breakdown.total_bookings} bookings discount
                    </span>
                    <Badge variant="outline" className="text-[10px] bg-green-100 text-green-700 border-green-200">
                      {breakdown.multi_booking_discount}% off
                    </Badge>
                  </div>
                )}
                {breakdown.same_hotel_bonus > 0 && (
                  <div className="flex justify-between text-xs">
                    <span className="text-muted-foreground">Same hotel bonus</span>
                    <Badge variant="outline" className="text-[10px] bg-green-100 text-green-700 border-green-200">
                      +{breakdown.same_hotel_bonus}%
                    </Badge>
                  </div>
                )}
                <Separator className="my-1" />
                <div className="flex justify-between text-sm font-semibold">
                  <span className="text-green-700 dark:text-green-400 flex items-center gap-1">
                    <TrendingDown className="w-3.5 h-3.5" strokeWidth={1.5} />
                    You save
                  </span>
                  <span className="mono text-green-700 dark:text-green-400" data-testid="cart-savings">
                    -{formatCartPrice(pricing.savings)}
                  </span>
                </div>
              </div>
            </div>
          </>        
        )}

        <Separator />

        {/* Total */}
        <div className="flex justify-between items-baseline">
          <span className="font-semibold heading-font">Total</span>
          <div className="text-right">
            <div className="text-2xl font-bold mono tabular-nums" data-testid="cart-total">
              {formatCartPrice(pricing.total)}
            </div>
            {hasDiscount && (
              <div className="text-xs text-muted-foreground line-through mono">
                {formatCartPrice(pricing.subtotal)}
              </div>
            )}
          </div>
        </div>

        {/* Discount Tiers Info */}
        {!hasDiscount && (
          <div className="bg-muted/50 rounded-lg p-3 text-xs text-muted-foreground">
            <p className="font-medium mb-1">💡 Save more with multiple bookings:</p>
            <ul className="space-y-0.5 ml-3">
              <li>• 2+ bookings: 5% off</li>
              <li>• 3+ bookings: 10% off</li>
              <li>• 5+ bookings: 15% off</li>
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
